import { useState, useEffect, useCallback } from 'react'
import { parseFlashcards, getParserStats } from '../utils/parser.js'
import ImportInstructions from './ImportInstructions.jsx'
import PersistedTextarea from './PersistedTextarea.jsx'
import ParserStatsPanel from './ParserStatsPanel.jsx'
import FeedbackBanner from './FeedbackBanner.jsx'
import ImportButton from './ImportButton.jsx'

/**
 * ImportForm component handles flashcard import functionality.
 * Provides textarea with parsing, validation, feedback, and submission.
 *
 * @param {Object} props - Component props
 * @param {Function} props.onImport - Callback to handle import (receives parsed flashcards array)
 * @param {string} props.userId - Current user ID for localStorage key
 * @param {number} props.currentFlashcardCount - Current number of flashcards user has
 * @returns {React.ReactElement} Import form
 */
export default function ImportForm({
  onImport,
  userId,
  currentFlashcardCount = 0,
}) {
  // Form state
  const [value, setValue] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Parser stats (updated on every change)
  const [parserStats, setParserStats] = useState({
    totalLines: 0,
    validCount: 0,
    invalidCount: 0,
    batchLimit: 40,
    globalLimit: 100,
    currentCount: 0,
    availableSlots: 100,
    limitReached: false,
    globalLimitReached: false,
  })

  // Feedback banner state
  const [feedback, setFeedback] = useState({
    visible: false,
    variant: 'success',
    message: '',
  })

  // Storage key for localStorage persistence
  const storageKey = userId ? `flashcards-import-${userId}` : null

  // Load persisted value from localStorage on mount
  useEffect(() => {
    if (storageKey) {
      try {
        const saved = localStorage.getItem(storageKey)
        if (saved) {
          setValue(saved)
        }
      } catch (error) {
        console.warn('Failed to load from localStorage:', error)
      }
    }
  }, [storageKey])

  // Update parser stats whenever value or currentFlashcardCount changes
  useEffect(() => {
    const stats = getParserStats(value, currentFlashcardCount)
    setParserStats(stats)
  }, [value, currentFlashcardCount])

  // Handle textarea change
  const handleChange = useCallback((newValue) => {
    setValue(newValue)
    // Clear feedback when user starts typing again
    setFeedback((prev) => (prev.visible ? { ...prev, visible: false } : prev))
  }, [])

  // Handle form submission
  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault()

      // Guard: Check if global limit is reached
      if (parserStats.globalLimitReached) {
        setFeedback({
          visible: true,
          variant: 'error',
          message:
            'Osiągnięto limit 100 fiszek. Usuń niektóre fiszki, aby dodać nowe.',
        })
        return
      }

      // Guard: Don't submit if no valid lines
      if (parserStats.validCount === 0) {
        setFeedback({
          visible: true,
          variant: 'error',
          message: 'Nie znaleziono poprawnych fiszek do zaimportowania.',
        })
        return
      }

      setIsSubmitting(true)
      setFeedback({ visible: false, variant: 'success', message: '' })

      try {
        // Parse flashcards (will respect both batch and global limits)
        const { valid } = parseFlashcards(value, currentFlashcardCount)

        // Call parent's import handler
        const result = await onImport(valid)

        if (result.success) {
          // Success - show feedback and clear textarea
          setFeedback({
            visible: true,
            variant: 'success',
            message: `Zaimportowano ${valid.length} ${valid.length === 1 ? 'fiszkę' : valid.length % 10 >= 2 && valid.length % 10 <= 4 && (valid.length % 100 < 10 || valid.length % 100 >= 20) ? 'fiszki' : 'fiszek'}!`,
          })

          // Clear textarea and localStorage
          setValue('')
          if (storageKey) {
            try {
              localStorage.removeItem(storageKey)
            } catch (error) {
              console.warn('Failed to clear localStorage:', error)
            }
          }
        } else {
          // Error from import
          setFeedback({
            visible: true,
            variant: 'error',
            message: result.error || 'Wystąpił błąd podczas importu.',
          })
        }
      } catch (error) {
        // Unexpected error
        setFeedback({
          visible: true,
          variant: 'error',
          message: 'Nieoczekiwany błąd. Spróbuj ponownie.',
        })
        console.error('Import form error:', error)
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      parserStats.validCount,
      parserStats.globalLimitReached,
      value,
      currentFlashcardCount,
      onImport,
      storageKey,
    ]
  )

  // Handle feedback banner close
  const handleFeedbackClose = useCallback(() => {
    setFeedback((prev) => ({ ...prev, visible: false }))
  }, [])

  return (
    <section className="import-section">
      <ImportInstructions />

      <form className="import-form" onSubmit={handleSubmit}>
        {/* Textarea with persistence */}
        <PersistedTextarea
          value={value}
          onChange={handleChange}
          storageKey={storageKey}
          placeholder="Wklej lub wpisz fiszki tutaj..."
          isDisabled={isSubmitting}
        />

        {/* Parser statistics */}
        {parserStats.totalLines > 0 && <ParserStatsPanel stats={parserStats} />}

        {/* Feedback banner */}
        <FeedbackBanner state={feedback} onClose={handleFeedbackClose} />

        {/* Submit button */}
        <ImportButton
          isSubmitting={isSubmitting}
          isDisabled={
            parserStats.validCount === 0 || parserStats.globalLimitReached
          }
        />
      </form>
    </section>
  )
}
